import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, UserPlus, Shield, Trash2, Search, Crown, Users } from "lucide-react";
import { fmtNum, shortDate } from "@/lib/format";

const PERM_KEYS = ["finance", "students", "transport", "merchants", "academics", "procurement", "settings"];

export function SuperadminSchoolAdminsConsole() {
  const qc = useQueryClient();
  const [schoolQ, setSchoolQ] = useState("");
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const { data: schools = [] } = useQuery({
    queryKey: ["sa-schools"],
    queryFn: async () => (await supabase.from("schools").select("id, name, region, district, student_count, active").order("name")).data ?? [],
  });

  const filteredSchools = useMemo(() => (schools as any[]).filter(s =>
    !schoolQ || s.name.toLowerCase().includes(schoolQ.toLowerCase()) || (s.district ?? "").toLowerCase().includes(schoolQ.toLowerCase())
  ), [schools, schoolQ]);

  const activeSchool = (schools as any[]).find(s => s.id === schoolId) ?? filteredSchools[0];
  const activeId = activeSchool?.id ?? null;

  const { data: admins = [], refetch } = useQuery({
    queryKey: ["school-admins", activeId],
    enabled: !!activeId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_school_admins", { _school_id: activeId! });
      if (error) throw error;
      return data as any[];
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("revoke_school_admin", { _membership_id: id });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Admin removed"); refetch(); qc.invalidateQueries({ queryKey: ["school-admins"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="grid gap-4 md:grid-cols-[320px_1fr]">
      <Card className="p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Schools</div>
          <Badge variant="outline">{fmtNum(schools.length)}</Badge>
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={schoolQ} onChange={e => setSchoolQ(e.target.value)} placeholder="Search schools" className="pl-8 h-8 text-xs" />
        </div>
        <div className="max-h-[520px] overflow-auto space-y-1">
          {filteredSchools.map(s => (
            <button key={s.id} onClick={() => setSchoolId(s.id)}
              className={`w-full text-left rounded-md border px-2.5 py-2 text-xs transition ${activeId === s.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
              <div className="font-medium truncate">{s.name}</div>
              <div className={`text-[10px] ${activeId === s.id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                {s.district ?? s.region ?? "—"} · {fmtNum(s.student_count ?? 0)} students
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        {activeSchool ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="font-display text-lg">{activeSchool.name}</div>
                <div className="text-xs text-muted-foreground">{activeSchool.district ?? "—"} · {(admins as any[]).length} admin(s)</div>
              </div>
              <Button size="sm" onClick={() => setAssignOpen(true)}><UserPlus className="mr-1 h-3.5 w-3.5" />Assign admin</Button>
            </div>

            <div className="divide-y rounded-md border">
              {(admins as any[]).map(a => (
                <div key={a.membership_id} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      {(a.full_name ?? "?").split(" ").map((n: string) => n[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {a.full_name ?? "Unnamed admin"}
                        {a.is_primary && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{a.title ?? "School admin"} · {a.phone ?? "—"}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {PERM_KEYS.filter(k => (a.permissions ?? {})[k]).map(k => (
                          <Badge key={k} variant="outline" className="text-[9px] capitalize">{k}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] text-muted-foreground">since {shortDate(a.created_at)}</div>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm("Remove this admin?")) revoke.mutate(a.membership_id); }}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
              {!(admins as any[]).length && <div className="p-8 text-center text-sm text-muted-foreground">No admins assigned. Click "Assign admin" above.</div>}
            </div>
          </>
        ) : <div className="p-8 text-center text-sm text-muted-foreground">Select a school on the left.</div>}
      </Card>

      <AssignAdminDialog open={assignOpen} onOpenChange={setAssignOpen} schoolId={activeId} onDone={() => refetch()} />
    </div>
  );
}

function AssignAdminDialog({ open, onOpenChange, schoolId, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; schoolId: string | null; onDone: () => void }) {
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [perms, setPerms] = useState<Record<string, boolean>>({ finance: true, students: true, transport: true, merchants: true, academics: true, procurement: true, settings: false });

  const { data: users = [] } = useQuery({
    queryKey: ["users-search", q],
    enabled: open,
    queryFn: async () => {
      let query = supabase.from("profiles").select("id, full_name, phone").limit(20);
      if (q) query = query.or(`full_name.ilike.%${q}%,phone.ilike.%${q}%`);
      return (await query).data ?? [];
    },
  });

  const assign = useMutation({
    mutationFn: async () => {
      if (!userId || !schoolId) throw new Error("Pick a user and school");
      const { error } = await supabase.rpc("assign_school_admin", {
        _user_id: userId, _school_id: schoolId,
        _permissions: perms as any, _is_primary: isPrimary, _title: title || undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Admin assigned"); onOpenChange(false); onDone(); setUserId(null); setTitle(""); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="h-4 w-4" />Assign school admin</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Search user</Label>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Name or phone" />
          </div>
          <div className="max-h-40 overflow-auto rounded border divide-y">
            {(users as any[]).map(u => (
              <button key={u.id} onClick={() => setUserId(u.id)} className={`w-full text-left px-3 py-2 text-sm ${userId === u.id ? "bg-primary/10" : "hover:bg-muted"}`}>
                <div className="font-medium">{u.full_name ?? "Unnamed"}</div>
                <div className="text-xs text-muted-foreground">{u.phone ?? "—"}</div>
              </button>
            ))}
            {!(users as any[]).length && <div className="p-3 text-center text-xs text-muted-foreground">No users found.</div>}
          </div>
          <div>
            <Label className="text-xs">Title (optional)</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Head of Finance, Deputy Head…" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="prim" checked={isPrimary} onCheckedChange={(v) => setIsPrimary(!!v)} />
            <Label htmlFor="prim" className="text-xs">Make this their primary school</Label>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Module access</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {PERM_KEYS.map(k => (
                <label key={k} className="flex items-center gap-2 text-xs capitalize">
                  <Checkbox checked={!!perms[k]} onCheckedChange={(v) => setPerms(p => ({ ...p, [k]: !!v }))} />
                  {k}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!userId || assign.isPending} onClick={() => assign.mutate()}>{assign.isPending ? "Assigning…" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
